import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Switch,
    Paper,
    Alert,
    CircularProgress,
    Chip,
    Divider,
} from '@mui/material';
import {
    MailOutline,
    EventAvailable,
    Payment,
    WorkspacePremium,
    Campaign,
    InfoOutlined,
} from '@mui/icons-material';
import userService from '../../services/userService';
import './EmailManagementSection.css';

const CATEGORY_KEYS = [
    'class_reminders',
    'payment_reminders',
    'certificate_notifications',
    'general_updates',
];

const PREFERENCE_META = {
    email_notifications: { Icon: MailOutline, accent: 'master' },
    class_reminders: { Icon: EventAvailable, accent: 'blue' },
    payment_reminders: { Icon: Payment, accent: 'green' },
    certificate_notifications: { Icon: WorkspacePremium, accent: 'gold' },
    general_updates: { Icon: Campaign, accent: 'purple' },
};

const DEFAULT_PREFERENCES = {
    email_notifications: true,
    class_reminders: true,
    payment_reminders: true,
    certificate_notifications: true,
    general_updates: true,
};

const EmailManagementSection = ({ userProfile, onProfileUpdate }) => {
    const { t } = useTranslation('profile');
    const [emailPreferences, setEmailPreferences] = useState(DEFAULT_PREFERENCES);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (userProfile) {
            setEmailPreferences({
                email_notifications: userProfile.email_notifications ?? true,
                class_reminders: userProfile.class_reminders ?? true,
                payment_reminders: userProfile.payment_reminders ?? true,
                certificate_notifications: userProfile.certificate_notifications ?? true,
                general_updates: userProfile.general_updates ?? true,
            });
        }
    }, [userProfile]);

    const emailsEnabled = emailPreferences.email_notifications;

    const statusLabel = useMemo(() => {
        if (!emailsEnabled) return t('emailSettings.statusOff');
        const enabledCount = CATEGORY_KEYS.filter((key) => emailPreferences[key]).length;
        if (enabledCount === CATEGORY_KEYS.length) return t('emailSettings.statusAll');
        if (enabledCount === 0) return t('emailSettings.statusOff');
        return t('emailSettings.statusCustom');
    }, [emailPreferences, emailsEnabled, t]);

    const buildUpdatedPreferences = (preference, value) => {
        const updated = { ...emailPreferences, [preference]: value };

        if (preference === 'email_notifications') {
            if (value) {
                CATEGORY_KEYS.forEach((key) => {
                    updated[key] = true;
                });
            } else {
                CATEGORY_KEYS.forEach((key) => {
                    updated[key] = false;
                });
            }
            return updated;
        }

        if (value) {
            updated.email_notifications = true;
        } else {
            const anyEnabled = CATEGORY_KEYS.some((key) => updated[key]);
            if (!anyEnabled) {
                updated.email_notifications = false;
            }
        }

        return updated;
    };

    const handlePreferenceChange = async (preference, value) => {
        const previousPreferences = emailPreferences;
        const updatedPreferences = buildUpdatedPreferences(preference, value);

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            setEmailPreferences(updatedPreferences);

            const response = await userService.updateEmailPreferences(updatedPreferences);

            if (onProfileUpdate) {
                onProfileUpdate(response);
            }

            setSuccess(t('emailSettings.updateSuccess'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Error updating email preferences:', err);
            setError(err.message || t('emailSettings.updateFailed'));
            setEmailPreferences(previousPreferences);
        } finally {
            setSaving(false);
        }
    };

    const renderPreferenceRow = (key, { isMaster = false } = {}) => {
        const meta = PREFERENCE_META[key];
        const Icon = meta.Icon;
        const disabled = saving || (!isMaster && !emailsEnabled);

        return (
            <div
                key={key}
                className={`email-preference-row ${isMaster ? 'email-preference-row--master' : ''} ${disabled && !isMaster ? 'email-preference-row--disabled' : ''}`}
            >
                <div className={`email-preference-icon email-preference-icon--${meta.accent}`}>
                    <Icon fontSize="small" />
                </div>

                <div className="email-preference-copy">
                    <Typography variant="subtitle1" className="email-preference-label">
                        {t(`emailSettings.options.${key}.label`)}
                    </Typography>
                    <Typography variant="body2" className="email-preference-description">
                        {t(`emailSettings.options.${key}.description`)}
                    </Typography>
                </div>

                <Switch
                    checked={Boolean(emailPreferences[key])}
                    onChange={(e) => handlePreferenceChange(key, e.target.checked)}
                    disabled={disabled}
                    color="primary"
                    inputProps={{
                        'aria-label': t(`emailSettings.options.${key}.label`),
                    }}
                />
            </div>
        );
    };

    if (!userProfile) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="email-management-section">
            <div className="section-header">
                <div className="email-settings-heading">
                    <div>
                        <h2>{t('emailSettings.title')}</h2>
                        <p className="section-description">{t('emailSettings.description')}</p>
                    </div>
                    <Chip
                        label={statusLabel}
                        size="small"
                        className={`email-settings-status email-settings-status--${emailsEnabled ? 'on' : 'off'}`}
                    />
                </div>
            </div>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Paper elevation={0} className="email-preferences-panel">
                {renderPreferenceRow('email_notifications', { isMaster: true })}

                <Divider className="email-preferences-divider" />

                <div className="email-categories-header">
                    <Typography variant="overline" className="email-categories-title">
                        {t('emailSettings.categoriesTitle')}
                    </Typography>
                    {!emailsEnabled && (
                        <Typography variant="caption" className="email-categories-hint">
                            {t('emailSettings.categoriesHint')}
                        </Typography>
                    )}
                </div>

                <div className={`email-category-list ${!emailsEnabled ? 'email-category-list--muted' : ''}`}>
                    {CATEGORY_KEYS.map((key) => renderPreferenceRow(key))}
                </div>
            </Paper>

            <div className="email-settings-footnote">
                <InfoOutlined fontSize="small" />
                <Typography variant="body2">{t('emailSettings.footnote')}</Typography>
            </div>

            {saving && (
                <Typography variant="caption" className="email-settings-saving">
                    {t('emailSettings.saving')}
                </Typography>
            )}
        </div>
    );
};

export default EmailManagementSection;
